'use client';

import { useMemo, useState } from 'react';
import { CalendarClock, Loader2, Repeat } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCustomerSession } from '@/lib/customer-bridge';

export type RecurringItem = {
  menuItemId: string;
  quantity: number;
  selectedTierId?: string;
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

function toHHmm(date: Date): string {
  const h = date.getHours().toString().padStart(2, '0');
  const m = date.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * "Make it recurring" — offered right after a scheduled pre-order is placed.
 * Saves the booking's line items as a recurring order; each occurrence is then
 * materialized as a slot-backed scheduled pre-order by the cron. Management
 * lives in the customer's Chow 'Me' area.
 */
export function RecurringOrderToggle({
  menuId,
  items,
  guestName,
  guestContact,
  guestNote,
  defaultStartsAt,
}: {
  menuId: string;
  items: RecurringItem[];
  guestName: string;
  guestContact: string;
  guestNote: string;
  /** The placed booking's pickup slot start, used for the sensible default. */
  defaultStartsAt?: string | null;
}) {
  const { status: authStatus, signIn } = useCustomerSession();
  const signedIn = authStatus === 'authenticated';

  const defaultDay = useMemo(() => {
    if (!defaultStartsAt) return 1;
    const date = new Date(defaultStartsAt);
    if (Number.isNaN(date.getTime())) return 1;
    return date.getDay();
  }, [defaultStartsAt]);
  const defaultTime = useMemo(() => {
    if (!defaultStartsAt) return '12:00';
    const date = new Date(defaultStartsAt);
    if (Number.isNaN(date.getTime())) return '12:00';
    return toHHmm(date);
  }, [defaultStartsAt]);

  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState<'weekly' | 'fortnightly' | 'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState<number>(defaultDay);
  const [dayOfMonth, setDayOfMonth] = useState<number>(Math.max(1, defaultDay + 1));
  const [time, setTime] = useState(defaultTime);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!signedIn) {
    return (
      <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--cs-border)]/70 bg-[var(--color-bg-sunken)]/30 p-4 text-left">
        <div className="flex items-start gap-2">
          <Repeat className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">Make this a regular booking?</p>
            <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-secondary)]">
              Save this as a recurring booking on your Chow account.
            </p>
          </div>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={signIn}>
          Sign in to make it recurring
        </Button>
      </div>
    );
  }

  async function handleSave() {
    if (items.length === 0) {
      toast.error('This booking has no items to repeat.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/customer-auth/recurring-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menuId,
          items,
          cadence,
          dayOfWeek: cadence === 'monthly' ? null : dayOfWeek,
          dayOfMonth: cadence === 'monthly' ? dayOfMonth : null,
          timeLocal: time,
          guestName: guestName.trim() || undefined,
          guestContact: guestContact.trim() || undefined,
          guestNote: guestNote.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        toast.error(data?.error || 'Could not save this recurring booking.');
        return;
      }
      setSaved(true);
      toast.success('Saved — you can pause or cancel it anytime from Chow › Me.');
    } catch {
      toast.error('Could not save this recurring booking right now.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--cs-border)]/70 bg-[var(--color-bg-sunken)]/30 p-4 text-left">
      <label className="flex cursor-pointer items-start gap-2">
        <Checkbox checked={enabled} onCheckedChange={(value) => setEnabled(Boolean(value))} />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-[var(--color-text-primary)]">Repeat this booking automatically</span>
          <span className="mt-0.5 block text-xs leading-5 text-[var(--color-text-secondary)]">
            We&apos;ll place it again on the schedule you pick — each time as a real booking against the
            branch&apos;s pickup slots.
          </span>
        </span>
      </label>

      {enabled && !saved ? (
        <div className="grid gap-3 rounded-lg border border-[var(--cs-border)]/70 bg-[var(--color-bg-base)]/60 p-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="recurring-cadence" className="mb-1 block text-xs font-medium">
                Frequency
              </Label>
              <Select value={cadence} onValueChange={(value) => setCadence(value as 'weekly' | 'fortnightly' | 'monthly')}>
                <SelectTrigger id="recurring-cadence" className="h-9 text-sm">
                  <SelectValue placeholder="Frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Every week</SelectItem>
                  <SelectItem value="fortnightly">Every other week</SelectItem>
                  <SelectItem value="monthly">Every month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="recurring-time" className="mb-1 block text-xs font-medium">
                Pickup time
              </Label>
              <input
                id="recurring-time"
                type="time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                className="h-9 w-full rounded-md border border-input bg-[var(--color-bg-base)] px-2 text-sm"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="recurring-day" className="mb-1 block text-xs font-medium">
              {cadence === 'monthly' ? 'Day of month' : 'Day of week'}
            </Label>
            {cadence === 'monthly' ? (
              <Select value={String(dayOfMonth)} onValueChange={(value) => setDayOfMonth(Number(value))}>
                <SelectTrigger id="recurring-day" className="h-9 text-sm">
                  <SelectValue placeholder="Day of month" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      {day}
                      {day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={String(dayOfWeek)} onValueChange={(value) => setDayOfWeek(Number(value))}>
                <SelectTrigger id="recurring-day" className="h-9 text-sm">
                  <SelectValue placeholder="Day of week" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button type="button" size="sm" className="w-full" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarClock className="mr-2 h-4 w-4" />}
            Save recurring booking
          </Button>
        </div>
      ) : null}

      {saved ? (
        <p className="text-xs font-medium text-[var(--color-success)]">
          Recurring booking saved — manage it anytime in Chow › Me.
        </p>
      ) : null}
    </div>
  );
}
